import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Email,
  GitHub,
  LinkedIn,
  Translate,
} from '@mui/icons-material';
import { FaCode, FaGlobe, FaGooglePlay } from 'react-icons/fa';
import ContactForm from '../dialogs/contactFormDialog';
import { MusicPlayerPanel } from '../dialogs/musicPlayerDialog';
import { projectsData } from './projectsData';
import type { ProjectMeta } from './projectsData';
import './Portfolio2D.css';

interface Portfolio2DProps {
  onSwitchTo3D: () => void;
}

function Portfolio2D({ onSwitchTo3D }: Portfolio2DProps) {
  const { t, i18n } = useTranslation();
  const [openProject, setOpenProject] = useState<ProjectMeta | null>(null);
  const [navExpanded, setNavExpanded] = useState(false);

  const isGerman = i18n.language?.toLowerCase().startsWith('de');
  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="portfolio2d">
      {/* Nav — collapsible */}
      <nav className="p2d-nav">
        <div className="p2d-nav-row">
          <button
            type="button"
            className="p2d-nav-toggle"
            onClick={() => setNavExpanded((e) => !e)}
            aria-expanded={navExpanded}
            aria-label={navExpanded ? t('portfolio2D.nav.closeMenu') : t('portfolio2D.nav.openMenu')}
            title={navExpanded ? t('portfolio2D.nav.closeMenu') : t('portfolio2D.nav.openMenu')}
          >
            {navExpanded ? '✕' : '☰'}
          </button>
          <button
            type="button"
            className="p2d-nav-mini-switch"
            onClick={onSwitchTo3D}
            title={t('portfolio2D.switchTo3D')}
            aria-label={t('portfolio2D.switchTo3D')}
          >
            🕹️
          </button>
        </div>

        {navExpanded && (
          <div className="p2d-nav-content">
            <span className="p2d-brand">FABIAN.EBERT</span>
            <div className="p2d-nav-links">
              <a href="#about" onClick={() => setNavExpanded(false)}>
                {t('portfolio2D.nav.about')}
              </a>
              <a href="#projects" onClick={() => setNavExpanded(false)}>
                {t('portfolio2D.nav.projects')}
              </a>
              <a href="#contact" onClick={() => setNavExpanded(false)}>
                {t('portfolio2D.nav.contact')}
              </a>
              <a href="#music" onClick={() => setNavExpanded(false)}>
                {t('portfolio2D.nav.music')}
              </a>
            </div>
            <div className="p2d-nav-actions">
              <div className="p2d-lang">
                <Translate fontSize="small" />
                <button
                  type="button"
                  className={isGerman ? 'active' : ''}
                  onClick={() => changeLanguage('de')}
                >
                  DE
                </button>
                <button
                  type="button"
                  className={isGerman ? '' : 'active'}
                  onClick={() => changeLanguage('en')}
                >
                  EN
                </button>
              </div>
              <button type="button" className="p2d-switch-btn" onClick={onSwitchTo3D}>
                🕹️ {t('portfolio2D.switchTo3D')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* About */}
      <section id="about" className="p2d-section p2d-about">
        <h2 className="p2d-section-title">{t('portfolio2D.about.title')}</h2>
        <div className="p2d-window">
          <div className="p2d-window-titlebar">
            <span>ABOUT.ME</span>
          </div>
          <div className="p2d-window-body">
            <img
              className="p2d-avatar"
              src={`${process.env.PUBLIC_URL}/profile.png`}
              alt={t('portfolio2D.about.avatarAlt')}
            />
            <div className="p2d-bio">
              <p>{t('characterBio.batch1')}</p>
              <p>{t('characterBio.batch2')}</p>
              <p>{t('characterBio.batch3')}</p>
              <p>{t('characterBio.batch4')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section id="projects" className="p2d-section">
        <h2 className="p2d-section-title">{t('portfolio2D.projects.title')}</h2>
        <div className="p2d-projects-grid">
          {projectsData.map((project) => (
            <article key={project.id} className="p2d-project-card">
              <button
                type="button"
                className="p2d-project-open"
                onClick={() => setOpenProject(project)}
              >
                <div className="p2d-project-thumb">
                  <img src={project.thumbnail} alt={t(project.titleKey)} loading="lazy" />
                </div>
                <div className="p2d-project-meta">
                  <div className="p2d-project-title">{t(project.titleKey)}</div>
                  <div className="p2d-project-desc">{t(project.descriptionKey)}</div>
                  <div className="p2d-project-tech">
                    {project.tech.map((tech) => (
                      <span key={tech}>{tech}</span>
                    ))}
                  </div>
                </div>
              </button>

              <div className="p2d-project-footer">
                <button
                  type="button"
                  className="p2d-project-view"
                  onClick={() => setOpenProject(project)}
                >
                  ▶ {t('portfolio2D.projects.viewProject')}
                </button>

              {(project.githubUrl || project.googlePlayUrl || project.websiteUrl) && (
                <div className="p2d-project-external">
                  {project.githubUrl && (
                    <a href={project.githubUrl} target="_blank" rel="noreferrer" title="GitHub">
                      <FaCode />
                    </a>
                  )}
                  {project.googlePlayUrl && (
                    <a
                      href={project.googlePlayUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Google Play"
                    >
                      <FaGooglePlay />
                    </a>
                  )}
                  {project.websiteUrl && (
                    <a href={project.websiteUrl} target="_blank" rel="noreferrer" title="Website">
                      <FaGlobe />
                    </a>
                  )}
                </div>
              )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="p2d-section">
        <h2 className="p2d-section-title">{t('portfolio2D.contact.title')}</h2>
        <div className="p2d-contact-grid">
          <div className="p2d-window">
            <div className="p2d-window-titlebar">
              <span>{t('portfolio2D.contact.details')}</span>
            </div>
            <div className="p2d-window-body p2d-contact-details">
              <a className="p2d-contact-link" href="mailto:fabian.ebert@online.de">
                <Email /> {t('portfolio2D.contact.emailLabel')}
              </a>
              <a
                className="p2d-contact-link"
                href="https://github.com/SirArchy"
                target="_blank"
                rel="noreferrer"
              >
                <GitHub /> {t('portfolio2D.contact.githubLabel')}
              </a>
              <a
                className="p2d-contact-link"
                href="https://www.linkedin.com/in/fabian-e-762b85244"
                target="_blank"
                rel="noreferrer"
              >
                <LinkedIn /> {t('portfolio2D.contact.linkedinLabel')}
              </a>
              <p className="p2d-contact-note">{t('portfolio2D.contact.note')}</p>
            </div>
          </div>

          <div className="p2d-window">
            <div className="p2d-window-titlebar">
              <span>{t('portfolio2D.contact.formTitle')}</span>
            </div>
            <div className="p2d-window-body">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Music */}
      <section id="music" className="p2d-section p2d-music">
        <h2 className="p2d-section-title">{t('portfolio2D.music.title')}</h2>
        <div className="p2d-window p2d-music-frame">
          <MusicPlayerPanel autoplayDemo={false} />
        </div>
      </section>

      <footer className="p2d-footer">
        <p>{t('portfolio2D.footer', { year: new Date().getFullYear() })}</p>
      </footer>

      {/* Project detail dialog — reuses the existing 3D portfolio dialogs */}
      {openProject && <openProject.DialogComponent open onClose={() => setOpenProject(null)} />}
    </div>
  );
}

export default Portfolio2D;
